import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { useListRecipes, useListFavorites } from "@workspace/api-client-react";
import type { RecipeRecord } from "@workspace/api-client-react";
import { Layout } from "@/components/Layout";
import { RecipeCard } from "@/components/RecipeCard";
import { AddRecipeFAB, AddRecipeHub } from "@/components/AddRecipeHub";
import { PullToRefresh } from "@/components/PullToRefresh";
import { BookOpen, Dices, ChefHat, Flame, Zap, Leaf, Heart } from "lucide-react";
import { Link } from "wouter";
import { cn } from "@/lib/utils";
import type { NutritionSummary } from "@/components/NutritionLabel";

const BASE = import.meta.env.BASE_URL;

const CATEGORY_COLORS: Record<string, string> = {
  "main dish":       "hsl(17 53% 51%)",
  "soup/stew":       "hsl(25 80% 45%)",
  bread:             "hsl(45 75% 55%)",
  dessert:           "hsl(350 55% 70%)",
  beverage:          "hsl(210 50% 60%)",
  salad:             "hsl(145 40% 45%)",
  appetizer:         "hsl(38 85% 55%)",
  side:              "hsl(90 30% 50%)",
  breakfast:         "hsl(50 90% 60%)",
  snack:             "hsl(38 75% 50%)",
  sauce:             "hsl(91 18% 36%)",
  "sauce/condiment": "hsl(91 18% 36%)",
};

const CUISINE_PALETTE = [
  "hsl(17 53% 51%)",
  "hsl(25 80% 45%)",
  "hsl(45 75% 55%)",
  "hsl(91 18% 36%)",
  "hsl(145 40% 45%)",
  "hsl(210 50% 60%)",
  "hsl(38 85% 55%)",
  "hsl(350 55% 70%)",
];

function getCategoryBg(cat?: string | null): string {
  if (!cat) return "hsl(30 15% 55%)";
  const k = cat.toLowerCase().trim();
  if (CATEGORY_COLORS[k]) return CATEGORY_COLORS[k];
  for (const [key, val] of Object.entries(CATEGORY_COLORS)) {
    if (k.includes(key) || key.includes(k)) return val;
  }
  return "hsl(30 15% 55%)";
}

function isDark(hsl: string): boolean {
  const m = hsl.match(/hsl\(\s*[\d.]+\s+[\d.]+%\s+([\d.]+)%/);
  if (!m) return true;
  return parseFloat(m[1]) < 55;
}

function formatTime(t?: string | null): string | null {
  if (!t) return null;
  return t.replace(/\bminutes?\b/i, "min").replace(/\bhours?\b/i, "hr");
}

function heroUrl(id: number): string {
  return `${BASE}api/recipes/hero-images/file/${id}-hero.jpg`;
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface HighlightCard {
  recipeId: number;
  title: string;
  heroImagePath?: string | null;
  calories: number;
  protein_g: number;
  total_fat_g: number;
  total_carbs_g: number;
}

interface NutritionHighlights {
  lowestCalorie: HighlightCard;
  highestProtein: HighlightCard;
  lowestFat: HighlightCard;
  quickHealthy: HighlightCard | null;
}

// ─── Random Recipe Picker ─────────────────────────────────────────────────────

function RandomPicker({ recipes }: { recipes: RecipeRecord[] }) {
  const [, navigate] = useLocation();
  const [filterCat, setFilterCat] = useState<string | null>(null);
  const [picked, setPicked] = useState<RecipeRecord | null>(null);
  const [spinning, setSpinning] = useState(false);
  const [heroFailed, setHeroFailed] = useState(false);
  const lastIdRef = useRef<number | null>(null);

  const categories = useMemo(() => {
    const s = new Set<string>();
    for (const r of recipes) if (r.category) s.add(r.category);
    return Array.from(s).sort();
  }, [recipes]);

  const pool = useMemo(() => {
    if (!filterCat) return recipes;
    return recipes.filter((r) => r.category === filterCat);
  }, [recipes, filterCat]);

  const pick = useCallback(() => {
    if (pool.length === 0) return;
    setSpinning(true);
    setHeroFailed(false);
    setTimeout(() => {
      let candidate: RecipeRecord;
      if (pool.length === 1) {
        candidate = pool[0];
      } else {
        const filtered = pool.filter((r) => r.id !== lastIdRef.current);
        candidate = filtered[Math.floor(Math.random() * filtered.length)];
      }
      lastIdRef.current = candidate.id;
      setPicked(candidate);
      setSpinning(false);
    }, 420);
  }, [pool]);

  const bg = getCategoryBg(picked?.category);
  const textColor = isDark(bg) ? "hsl(40 33% 97%)" : "hsl(33 28% 14%)";
  const letter = picked
    ? (picked.title.match(/[A-Za-z0-9]/)?.[0] ?? picked.title[0] ?? "?").toUpperCase()
    : null;

  return (
    <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      <h2
        className="text-2xl font-bold text-foreground mb-4"
        style={{ fontFamily: "var(--font-display)" }}
      >
        What should we make?
      </h2>

      {categories.length > 1 && (
        <div className="overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 mb-4">
          <div className="flex gap-2 min-w-max">
            <button
              onClick={() => setFilterCat(null)}
              className={cn(
                "px-3 py-1 rounded-full text-xs font-medium border transition-all",
                filterCat === null
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-white text-muted-foreground border-border hover:border-primary/40 hover:text-foreground"
              )}
            >
              Any
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilterCat((p) => (p === cat ? null : cat))}
                className={cn(
                  "px-3 py-1 rounded-full text-xs font-medium border transition-all capitalize",
                  filterCat === cat
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-white text-muted-foreground border-border hover:border-primary/40 hover:text-foreground"
                )}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-3xl overflow-hidden border border-border/50 shadow-sm bg-white">
        <div
          className={cn(
            "transition-all duration-300",
            spinning ? "opacity-0 scale-95" : "opacity-100 scale-100"
          )}
        >
          {picked ? (
            <div className="flex flex-col sm:flex-row">
              <div
                className="relative sm:w-64 h-48 sm:h-auto flex-shrink-0 overflow-hidden flex items-center justify-center"
                style={{ backgroundColor: bg }}
              >
                {picked.heroImagePath && !heroFailed ? (
                  <img
                    src={heroUrl(picked.id)}
                    alt={picked.title}
                    className="w-full h-full object-cover"
                    onError={() => setHeroFailed(true)}
                  />
                ) : (
                  <span
                    className="text-8xl font-bold select-none opacity-30"
                    style={{ fontFamily: "var(--font-display)", color: textColor }}
                  >
                    {letter}
                  </span>
                )}
                {picked.category && (
                  <span
                    className="absolute bottom-3 left-3 px-3 py-1 rounded-full text-xs font-semibold"
                    style={{ backgroundColor: "rgba(255,255,255,0.92)", color: bg }}
                  >
                    {picked.category}
                  </span>
                )}
              </div>
              <div className="flex-1 p-6 flex flex-col justify-between">
                <div>
                  <h3
                    className="text-2xl font-bold text-foreground mb-1 leading-snug"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    {picked.title}
                  </h3>
                  {(picked.authorSource || picked.cuisine) && (
                    <p
                      className="text-sm text-muted-foreground italic mb-3"
                      style={{ fontFamily: "var(--font-handwritten)", fontSize: "0.95rem" }}
                    >
                      {[picked.authorSource, picked.cuisine].filter(Boolean).join(" · ")}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {formatTime(picked.prepTime) && (
                      <span className="bg-muted px-2 py-0.5 rounded-full">
                        Prep: {formatTime(picked.prepTime)}
                      </span>
                    )}
                    {formatTime(picked.cookTime) && (
                      <span className="bg-muted px-2 py-0.5 rounded-full">
                        Cook: {formatTime(picked.cookTime)}
                      </span>
                    )}
                    {picked.yieldServings && (
                      <span className="bg-muted px-2 py-0.5 rounded-full">
                        Serves {picked.yieldServings}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => navigate(`/recipe/${picked.id}`)}
                    className="flex-1 bg-primary text-primary-foreground rounded-xl py-2.5 text-sm font-semibold transition-all hover:opacity-90 active:scale-95"
                  >
                    Let's Cook!
                  </button>
                  <button
                    onClick={pick}
                    className="flex-1 bg-muted text-foreground rounded-xl py-2.5 text-sm font-semibold transition-all hover:bg-muted/80 active:scale-95 border border-border/50"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 gap-4 text-center px-6">
              <div
                className="w-20 h-20 rounded-2xl flex items-center justify-center"
                style={{ background: "hsl(17 53% 51% / 0.1)" }}
              >
                <Dices className="w-10 h-10 text-primary" />
              </div>
              <div>
                <p
                  className="text-xl font-bold text-foreground"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  Feeling indecisive?
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Let us pick something delicious for you.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-border/40 px-6 py-4 flex justify-center bg-muted/30">
          <button
            onClick={pick}
            disabled={pool.length === 0}
            className={cn(
              "inline-flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all",
              pool.length === 0
                ? "opacity-40 cursor-not-allowed bg-muted text-muted-foreground"
                : "bg-primary text-primary-foreground hover:opacity-90 active:scale-95 shadow-sm"
            )}
          >
            <Dices className="w-4 h-4" />
            Surprise Me!
          </button>
        </div>
      </div>
    </section>
  );
}

// ─── Cuisine Breakdown ────────────────────────────────────────────────────────

function CuisineBreakdown({ recipes }: { recipes: RecipeRecord[] }) {
  const [, navigate] = useLocation();

  const cuisineCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of recipes) {
      if (r.cuisine) counts.set(r.cuisine, (counts.get(r.cuisine) ?? 0) + 1);
    }
    const sorted = Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1]);

    const mainCuisines = sorted.filter(([, c]) => c >= 2);
    const otherCount = sorted
      .filter(([, c]) => c < 2)
      .reduce((s, [, c]) => s + c, 0);

    if (otherCount > 0) mainCuisines.push(["Other", otherCount]);

    return mainCuisines;
  }, [recipes]);

  const distinctCuisines = useMemo(() => {
    const s = new Set<string>();
    for (const r of recipes) if (r.cuisine) s.add(r.cuisine);
    return s.size;
  }, [recipes]);

  if (distinctCuisines < 3) return null;

  const max = Math.max(...cuisineCounts.map(([, c]) => c), 1);

  return (
    <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      <h2
        className="text-2xl font-bold text-foreground mb-4"
        style={{ fontFamily: "var(--font-display)" }}
      >
        Recipes by Cuisine
      </h2>
      <div className="bg-white rounded-2xl border border-border/50 shadow-sm p-6 space-y-3">
        {cuisineCounts.map(([cuisine, count], idx) => {
          const color = CUISINE_PALETTE[idx % CUISINE_PALETTE.length];
          const pct = (count / max) * 100;
          return (
            <button
              key={cuisine}
              onClick={() => {
                if (cuisine !== "Other") navigate(`/search?cuisine=${encodeURIComponent(cuisine)}`);
              }}
              disabled={cuisine === "Other"}
              className={cn(
                "w-full text-left group transition-all",
                cuisine !== "Other" && "hover:opacity-90 cursor-pointer"
              )}
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className="text-sm font-medium text-foreground group-hover:text-primary transition-colors"
                >
                  {cuisine}
                </span>
                <span className="text-sm text-muted-foreground">
                  {count} recipe{count !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="h-6 rounded-lg overflow-hidden bg-muted">
                <div
                  className="h-full rounded-lg transition-all duration-700"
                  style={{ width: `${pct}%`, backgroundColor: color }}
                />
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

// ─── Nutrition Highlight Cards ─────────────────────────────────────────────────

function NutritionCardThumb({
  id,
  title,
  heroImagePath,
}: {
  id: number;
  title: string;
  heroImagePath?: string | null;
}) {
  const [failed, setFailed] = useState(false);
  const letter = (title.match(/[A-Za-z0-9]/)?.[0] ?? title[0] ?? "?").toUpperCase();
  return (
    <div className="aspect-[4/3] overflow-hidden bg-muted flex items-center justify-center relative">
      {heroImagePath && !failed ? (
        <img
          src={heroUrl(id)}
          alt={title}
          className="w-full h-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <span
          className="text-5xl font-bold select-none opacity-20"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {letter}
        </span>
      )}
    </div>
  );
}

const HIGHLIGHT_CONFIGS = [
  {
    key: "lowestCalorie" as const,
    label: "Lowest Calorie",
    icon: Flame,
    color: "hsl(17 53% 51%)",
    metric: (c: HighlightCard) => `${c.calories} cal`,
    iconBg: "hsl(17 53% 51% / 0.12)",
  },
  {
    key: "highestProtein" as const,
    label: "Most Protein",
    icon: ChefHat,
    color: "hsl(210 50% 50%)",
    metric: (c: HighlightCard) => `${c.protein_g}g protein`,
    iconBg: "hsl(210 50% 50% / 0.12)",
  },
  {
    key: "lowestFat" as const,
    label: "Lightest",
    icon: Leaf,
    color: "hsl(145 40% 40%)",
    metric: (c: HighlightCard) => `${c.total_fat_g}g fat`,
    iconBg: "hsl(145 40% 40% / 0.12)",
  },
  {
    key: "quickHealthy" as const,
    label: "Quick & Healthy",
    icon: Zap,
    color: "hsl(38 85% 45%)",
    metric: (c: HighlightCard) => `${c.protein_g}g protein`,
    iconBg: "hsl(38 85% 45% / 0.12)",
  },
];

function NutritionHighlightCards({
  highlights,
  loading,
}: {
  highlights: NutritionHighlights | null;
  loading: boolean;
}) {
  const [, navigate] = useLocation();

  if (!loading && !highlights) return null;

  return (
    <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      <h2
        className="text-2xl font-bold text-foreground mb-4"
        style={{ fontFamily: "var(--font-display)" }}
      >
        By the Numbers
      </h2>
      <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 pb-2">
        <div className="flex gap-4 lg:grid lg:grid-cols-4" style={{ minWidth: "max-content" }}>
          {HIGHLIGHT_CONFIGS.map((cfg) => {
            const card = highlights?.[cfg.key] ?? null;
            const Icon = cfg.icon;

            if (loading || !card) {
              return (
                <div
                  key={cfg.key}
                  className="w-56 flex-shrink-0 rounded-2xl bg-white border border-border/50 shadow-sm overflow-hidden animate-pulse"
                >
                  <div className="aspect-[4/3] bg-muted" />
                  <div className="p-4">
                    <div className="w-8 h-8 rounded-lg bg-muted mb-3" />
                    <div className="h-3 rounded-full bg-muted w-2/3 mb-2" />
                    <div className="h-4 rounded-full bg-muted w-full mb-1" />
                    <div className="h-3 rounded-full bg-muted w-1/2" />
                  </div>
                </div>
              );
            }

            return (
              <button
                key={cfg.key}
                onClick={() => navigate(`/recipe/${card.recipeId}`)}
                className="w-56 flex-shrink-0 rounded-2xl bg-white border border-border/50 shadow-sm overflow-hidden text-left transition-all hover:shadow-md hover:-translate-y-0.5 hover:border-primary/30 active:scale-95"
              >
                <NutritionCardThumb
                  id={card.recipeId}
                  title={card.title}
                  heroImagePath={card.heroImagePath}
                />
                <div className="p-4">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center mb-3"
                    style={{ backgroundColor: cfg.iconBg }}
                  >
                    <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                  </div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">{cfg.label}</p>
                  <p
                    className="text-sm font-semibold text-foreground leading-snug line-clamp-2 mb-2"
                    style={{ fontFamily: "var(--font-display)" }}
                  >
                    {card.title}
                  </p>
                  <p className="text-xs font-bold" style={{ color: cfg.color }}>
                    {cfg.metric(card)}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Category Browse ───────────────────────────────────────────────────────────

function CategoryBrowse({ recipes }: { recipes: RecipeRecord[] }) {
  const [, navigate] = useLocation();

  const categories = useMemo(() => {
    const map = new Map<string, RecipeRecord[]>();
    for (const r of recipes) {
      if (!r.category) continue;
      const arr = map.get(r.category) ?? [];
      arr.push(r);
      map.set(r.category, arr);
    }
    return Array.from(map.entries())
      .sort((a, b) => b[1].length - a[1].length)
      .map(([name, items]) => ({ name, count: items.length, items }));
  }, [recipes]);

  if (categories.length === 0) return null;

  return (
    <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      <h2
        className="text-2xl font-bold text-foreground mb-4"
        style={{ fontFamily: "var(--font-display)" }}
      >
        Browse by Category
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {categories.map(({ name, count, items }) => {
          const bg = getCategoryBg(name);
          const thumbs = items.filter((r) => r.heroImagePath).slice(0, 3);

          return (
            <button
              key={name}
              onClick={() => navigate(`/search?category=${encodeURIComponent(name)}`)}
              className="rounded-2xl overflow-hidden border border-border/40 shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 active:scale-95 text-left group"
            >
              <div
                className="relative h-24 flex items-end px-4 pb-3"
                style={{ backgroundColor: bg }}
              >
                <div className="flex gap-1 absolute top-3 right-3">
                  {thumbs.slice(0, 3).map((r) => (
                    <div key={r.id} className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 shadow-sm">
                      <ThumbSilent id={r.id} title={r.title} category={r.category} />
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white px-4 py-3">
                <p
                  className="font-semibold text-sm text-foreground capitalize leading-snug group-hover:text-primary transition-colors"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  {name}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {count} recipe{count !== 1 ? "s" : ""}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function ThumbSilent({
  id,
  title,
  category,
}: {
  id: number;
  title: string;
  category?: string | null;
}) {
  const [failed, setFailed] = useState(false);
  const bg = getCategoryBg(category);
  const letter = (title.match(/[A-Za-z0-9]/)?.[0] ?? title[0] ?? "?").toUpperCase();
  const textColor = isDark(bg) ? "hsl(40 33% 97%)" : "hsl(33 28% 14%)";
  return (
    <div className="w-full h-full" style={{ backgroundColor: bg }}>
      {!failed ? (
        <img
          src={heroUrl(id)}
          alt={title}
          className="w-full h-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <span
            className="text-xs font-bold opacity-50"
            style={{ color: textColor, fontFamily: "var(--font-display)" }}
          >
            {letter}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Your Favorites ───────────────────────────────────────────────────────────

function FavoritesRow({
  nutritionMap,
}: {
  nutritionMap: Map<number, NutritionSummary>;
}) {
  const { data, isLoading } = useListFavorites();
  const favs = data?.recipes ?? [];

  if (isLoading || favs.length === 0) return null;

  return (
    <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h2
          className="text-2xl font-bold text-foreground flex items-center gap-2"
          style={{ fontFamily: "var(--font-display)" }}
        >
          <Heart className="w-5 h-5 text-rose-500 fill-rose-500" />
          Your Favorites
        </h2>
        <Link
          href="/favorites"
          className="text-sm font-medium text-primary hover:underline"
        >
          See all
        </Link>
      </div>
      <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 pb-2">
        <div className="flex gap-4" style={{ minWidth: "max-content" }}>
          {favs.slice(0, 8).map((r) => (
            <div key={r.id} className="w-64 flex-shrink-0">
              <RecipeCard recipe={r} nutrition={nutritionMap.get(r.id) ?? null} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Recent Additions ─────────────────────────────────────────────────────────

function RecentAdditions({
  recipes,
  nutritionMap,
}: {
  recipes: RecipeRecord[];
  nutritionMap: Map<number, NutritionSummary>;
}) {
  const recent = useMemo(() => {
    return [...recipes]
      .sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
      })
      .slice(0, 5);
  }, [recipes]);

  if (recent.length === 0) return null;

  return (
    <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-10">
      <h2
        className="text-2xl font-bold text-foreground mb-4"
        style={{ fontFamily: "var(--font-display)" }}
      >
        Recent Additions
      </h2>
      <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 pb-2">
        <div className="flex gap-4" style={{ minWidth: "max-content" }}>
          {recent.map((r) => (
            <div key={r.id} className="w-64 flex-shrink-0">
              <RecipeCard recipe={r} nutrition={nutritionMap.get(r.id) ?? null} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Skeleton loading sections ─────────────────────────────────────────────────

function SectionSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-10 rounded-xl bg-muted animate-pulse" />
      ))}
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

interface NutritionSummaryRow {
  recipeId: number;
  perServing: Record<string, number>;
  confidence: string | null;
  servingsUsed: number | null;
}

function toNutritionSummary(row: NutritionSummaryRow): NutritionSummary {
  const ps = row.perServing;
  return {
    calories: typeof ps.calories === "number" ? Math.round(ps.calories) : 0,
    protein_g: typeof ps.protein_g === "number" ? Math.round(ps.protein_g * 10) / 10 : 0,
    total_carbs_g: typeof ps.total_carbs_g === "number" ? Math.round(ps.total_carbs_g * 10) / 10 : 0,
    total_fat_g: typeof ps.total_fat_g === "number" ? Math.round(ps.total_fat_g * 10) / 10 : 0,
  };
}

export default function CookbookHomePage() {
  const [hubOpen, setHubOpen] = useState(false);
  const { data, isLoading, refetch: refetchRecipes } = useListRecipes();
  const recipes: RecipeRecord[] = data?.recipes ?? [];

  const [highlights, setHighlights] = useState<NutritionHighlights | null>(null);
  const [highlightsLoading, setHighlightsLoading] = useState(true);
  const [nutritionMap, setNutritionMap] = useState<Map<number, NutritionSummary>>(new Map());

  const fetchHighlights = useCallback(() => {
    setHighlightsLoading(true);
    return fetch(`${BASE}api/nutrition/highlights`)
      .then((r) => r.json())
      .then((d) => {
        setHighlights(d.highlights ?? null);
        setHighlightsLoading(false);
      })
      .catch(() => {
        setHighlights(null);
        setHighlightsLoading(false);
      });
  }, []);

  const fetchSummaries = useCallback(() => {
    const base = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";
    return fetch(`${base}/api/nutrition/summaries`)
      .then((r) => r.json())
      .then((d: { summaries?: NutritionSummaryRow[] }) => {
        const map = new Map<number, NutritionSummary>();
        for (const row of d.summaries ?? []) {
          map.set(row.recipeId, toNutritionSummary(row));
        }
        setNutritionMap(map);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchHighlights();
  }, [fetchHighlights]);

  useEffect(() => {
    fetchSummaries();
  }, [fetchSummaries]);

  const handleRefresh = useCallback(
    () => Promise.all([refetchRecipes(), fetchHighlights(), fetchSummaries()]),
    [refetchRecipes, fetchHighlights, fetchSummaries]
  );

  return (
    <Layout>
      <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-10">
        <div
          className="relative overflow-hidden px-8 py-12 text-center"
          style={{
            background:
              "linear-gradient(135deg, hsl(40 33% 94%) 0%, hsl(17 30% 92%) 50%, hsl(91 15% 90%) 100%)",
          }}
        >
          <div className="relative z-10 max-w-7xl mx-auto">
            <div className="flex justify-center mb-4">
              <div className="bg-primary/10 p-4 rounded-2xl">
                <BookOpen className="w-8 h-8 text-primary" />
              </div>
            </div>
            <h1
              className="text-4xl md:text-5xl font-bold text-foreground mb-3"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Family Cookbook
            </h1>
            <p className="text-muted-foreground text-lg max-w-md mx-auto">
              {isLoading ? (
                "Loading your recipes…"
              ) : (
                <>
                  {recipes.length} recipe{recipes.length !== 1 ? "s" : ""}{" "}
                  passed down through the family
                </>
              )}
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            <SectionSkeleton rows={4} />
          </div>
        ) : recipes.length === 0 ? (
          <div className="px-4 sm:px-6 lg:px-8 max-w-2xl mx-auto py-10">
            <div className="rounded-3xl border border-border/50 bg-white shadow-sm px-8 py-12 text-center space-y-4">
              <div className="mx-auto w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                <BookOpen className="w-8 h-8 text-primary" />
              </div>
              <h2
                className="text-2xl font-bold text-foreground"
                style={{ fontFamily: "var(--font-display)" }}
              >
                Your cookbook is empty
              </h2>
              <p className="text-muted-foreground text-sm max-w-sm mx-auto">
                Add your first recipe to get started — type it in, scan a page, or upload a photo.
              </p>
              <button
                type="button"
                onClick={() => setHubOpen(true)}
                className="inline-flex items-center justify-center rounded-xl bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90 active:scale-95"
              >
                Add your first recipe
              </button>
            </div>
          </div>
        ) : (
          <>
            <RandomPicker recipes={recipes} />
            <FavoritesRow nutritionMap={nutritionMap} />
            <CuisineBreakdown recipes={recipes} />
            <NutritionHighlightCards highlights={highlights} loading={highlightsLoading} />
            <CategoryBrowse recipes={recipes} />
            <RecentAdditions recipes={recipes} nutritionMap={nutritionMap} />
          </>
        )}
      </div>

      </PullToRefresh>
      <AddRecipeFAB onClick={() => setHubOpen(true)} />
      <AddRecipeHub open={hubOpen} onClose={() => setHubOpen(false)} />
    </Layout>
  );
}
